import { Fragment, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Label from '../../components/Label'
import SectionHeader from '../../components/SectionHeader'
import InfoButton from '../../components/InfoButton'
import LearnMore from '../../components/LearnMore'
import Page from '../../components/Page'
import Divider from '@mui/material/Divider'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Prompt from '../../components/Prompt'
import useContentful from '../../contexts/useContentful'
import useQualtricsSurvey from '../../hooks/useQualtricsSurvey'
// import useRecaptcha from "../../hooks/useRecaptcha";

const NEXT_INDEX = {
  'housing': 7,
  'water': 8,
  'transportation': 9,
  'open-space-and-recreation': 10,
}

const NEXT_PAGE = {
  'housing': surveyID => `/big-ideas/${surveyID}/water`,
  'water': surveyID => `/big-ideas/${surveyID}/transportation`,
  'transportation': surveyID => `/big-ideas/${surveyID}/open-space-and-recreation`,
  'open-space-and-recreation': surveyID => `/demographics`,
}

const BigMovesPage = () => {
  const navigate = useNavigate()
  const { pageID, surveyID } = useParams()
  const responses = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.responses)
  const { 
    advanceSurvey,
    submitSingleAnswer,
    submitMultiSelectAnswer,
    qualtricsLoading,
  } = useQualtricsSurvey()
  const {
    bigIdeas,
    buttonText,
    description,
    instruction,
    textFieldLabel,
    textFieldPlaceholder,
    qualtricsId,
    commentQualtricsId,
    learnMoreText,
  } = useContentful()
  const [value, setValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // const { executeRecaptcha } = useRecaptcha();

  const onSubmit = async () => {
    setIsLoading(true)
    // const actionName = `advance_big_moves-${pageID}`.replace(/-/g, '_')
    // const response = await executeRecaptcha(actionName)
    advanceSurvey({
      // score: response?.score,
      nextIndex: NEXT_INDEX[pageID],
      cb: error => {
        setIsLoading(false)
        if (!error) navigate(NEXT_PAGE[pageID]?.(surveyID))
      },
    }, commentQualtricsId, value)
  }

  useEffect(() => {
    if (value.length) setValue('')
  }, [pageID])

  return (
    <Page>
      <Stack>
        <Container>
          <Stack
            display="grid"
            py={6}
            gap={6}
          >
            <SectionHeader />
            <Stack
              gap={6}
              textAlign="center"
              maxWidth="600px"
              justifySelf="center"
            >
              <Typography
                variant="body1"
              >
                {description}
              </Typography>
              <Typography
                variant="bigLabel"
                color="text.neutralDark"
              >
                {instruction}
              </Typography>
            </Stack>
            <Stack>
              {bigIdeas?.map(({ fields }, idx) => (
                <Fragment
                  key={fields.entryTitle}
                >
                  {idx === 0 && (
                    <Divider 
                      light
                    />
                  )}
                  <Stack
                    display="grid"
                    gridTemplateColumns={{
                      xs: '1fr auto',
                      sm: 'auto 1fr auto',
                    }}
                    gap={2}
                    alignItems="center"
                    py={{
                      xs: 1.5,
                      sm: 3,
                    }}
                    px={{
                      sm: 1.5,
                    }}
                  >
                    <Switch
                      checked={!!responses[`${qualtricsId}_${fields.qualtricsId}`]}
                      onChange={() => submitMultiSelectAnswer(qualtricsId, fields.qualtricsId, !!!responses?.[`${qualtricsId}_${fields.qualtricsId}`], responses)}
                    />
                    <Stack
                      gap={.5}
                      sx={{
                        order: {
                          xs: -1,
                          sm: 'initial',
                        },
                      }}
                    >
                      <Typography>
                        {fields.label}
                      </Typography>
                      <LearnMore
                        tooltipText={fields.tooltip}
                        sx={{
                          display: {
                            sm: 'none',
                          },
                        }}
                        buttonText={learnMoreText}
                      />
                    </Stack>
                    <InfoButton
                      size="lg"
                      tooltipText={fields.tooltip}
                      sx={{
                        display: {
                          xs: 'none',
                          sm: 'initial',
                        },
                      }}
                    />
                  </Stack>
                  <Divider
                    light
                  />
                </Fragment>
              ))}
            </Stack>
            <Stack
              gap={1}
            >
              <Label>
                {textFieldLabel}
              </Label>
              <TextField
                multiline
                rows={3}
                placeholder={textFieldPlaceholder}
                value={value}
                onChange={e => {
                  setValue(e.target.value)
                  // debouncedSubmitTextAnswer(commentQualtricsId, e.target.value)
                }}
              />
            </Stack>
            <Stack
              display="grid"
              justifyContent="center"
            >
              <Button
                sx={{
                  position: 'relative',
                }}
                disabled={(isLoading || qualtricsLoading)}
                onClick={onSubmit}
              >
                <span
                  style={{
                    opacity: (isLoading || qualtricsLoading) ? 0 : 1,
                  }}
                >
                  {buttonText}
                </span>
                {(isLoading || qualtricsLoading) && (
                  <CircularProgress 
                    size={25}
                    sx={{
                      position: 'absolute',
                    }}
                  />
                )}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Stack>
      <Prompt />
    </Page>
  )
}

export default BigMovesPage
